<!--
  A review surface, not a directory to browse: the picker (MerchantCombobox) is where a
  merchant is actually used day to day (AGENTS.md's captain note on this feature). This
  screen catches a payee directory that has grown wrong - a typo that split "Lidl" into two
  rows, a name nobody recognises - and repairs it: aliases stop the next split, merging
  repairs an existing one. Both are gated on `canManage`; a viewer without RECORDS_MANAGE
  sees the same flat list the contract's `merchants_list` alone used to allow.
-->
<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Card } from '@taler/ui'
import { formatInstantDate } from '@taler/domain'
import type { Merchant } from '../composables/use-merchants.ts'
import type { MerchantMergeResult } from '../composables/use-merchant-actions.ts'
import MerchantAliases from '@/features/merchants/components/MerchantAliases.vue'
import MerchantMergeDialog from '@/features/merchants/components/MerchantMergeDialog.vue'

const props = defineProps<{
  merchants: readonly Merchant[]
  hasNextPage: boolean
  isFetchingNextPage: boolean
  canManage: boolean
  addAlias: (merchantId: string, alias: string) => Promise<Merchant>
  removeAlias: (merchantId: string, alias: string) => Promise<Merchant>
  countEntries: (sourceId: string, signal?: AbortSignal) => Promise<number>
  merge: (sourceId: string, destinationId: string) => Promise<MerchantMergeResult>
}>()

const emit = defineEmits<{ fetchNextPage: [] }>()

const { t, locale } = useI18n()

const expanded = ref<string | undefined>(undefined)
const mergeSource = ref<Merchant | undefined>(undefined)
const mergeNotice = ref<string | undefined>(undefined)

function toggleExpanded(merchantId: string): void {
  expanded.value = expanded.value === merchantId ? undefined : merchantId
}

function openMerge(merchant: Merchant): void {
  mergeNotice.value = undefined
  mergeSource.value = merchant
}

function closeMerge(): void {
  mergeSource.value = undefined
}

function onMerged(result: MerchantMergeResult): void {
  const source = mergeSource.value
  const destination = props.merchants.find((candidate) => candidate.id === result.destinationId)
  mergeSource.value = undefined
  if (source === undefined) return
  mergeNotice.value = t('merchants.mergeSuccess', {
    source: source.name,
    destination: destination?.name ?? '',
  })
}
</script>

<template>
  <p v-if="mergeNotice !== undefined" role="status" class="text-[color:var(--color-text)]">
    {{ mergeNotice }}
  </p>

  <Card padding="none" class="overflow-x-auto">
    <table class="w-full min-w-[32rem] border-collapse">
      <thead>
        <tr class="table-head">
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('merchants.name') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('merchants.firstSeen') }}
          </th>
          <th v-if="canManage" class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            <span class="sr-only">{{ t('merchants.aliases') }} / {{ t('merchants.merge') }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="merchant in merchants" :key="merchant.id">
          <tr class="border-b border-[color:var(--color-border)]">
            <td
              class="px-[var(--space-cell)] py-[var(--space-inline)] font-medium text-[color:var(--color-text)]"
            >
              {{ merchant.name }}
            </td>
            <td class="px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text-muted)]">
              {{ formatInstantDate(merchant.createdAt, locale as 'en' | 'hu') }}
            </td>
            <td v-if="canManage" class="px-[var(--space-cell)] py-[var(--space-inline)]">
              <div class="flex items-center justify-end gap-[var(--space-inline)]">
                <button
                  type="button"
                  class="text-[length:var(--text-step-1)] text-[color:var(--color-accent)]"
                  @click="toggleExpanded(merchant.id)"
                >
                  {{
                    expanded === merchant.id
                      ? t('merchants.aliasesToggleHide')
                      : `${t('merchants.aliasesToggleShow')} (${merchant.aliases.length})`
                  }}
                </button>
                <button
                  type="button"
                  class="text-[length:var(--text-step-1)] text-[color:var(--color-text)]"
                  @click="openMerge(merchant)"
                >
                  {{ t('merchants.merge') }}
                </button>
              </div>
            </td>
          </tr>
          <tr
            v-if="canManage && expanded === merchant.id"
            class="border-b border-[color:var(--color-border)]"
          >
            <td
              :colspan="3"
              class="bg-[color:var(--color-surface-sunken)] px-[var(--space-cell)] py-[var(--space-cell)]"
            >
              <MerchantAliases
                :merchant="merchant"
                :add-alias="(alias) => addAlias(merchant.id, alias)"
                :remove-alias="(alias) => removeAlias(merchant.id, alias)"
              />
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </Card>
  <button
    v-if="hasNextPage"
    type="button"
    :disabled="isFetchingNextPage"
    class="min-h-[var(--density-control)] self-start rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] py-[var(--space-inline)] text-[length:var(--text-step-3)] text-[color:var(--color-text)] disabled:opacity-60"
    @click="emit('fetchNextPage')"
  >
    {{ isFetchingNextPage ? t('merchants.loadingMore') : t('merchants.loadMore') }}
  </button>

  <MerchantMergeDialog
    :open="mergeSource !== undefined"
    :source="mergeSource"
    :candidates="merchants"
    :count-entries="countEntries"
    :merge="merge"
    @close="closeMerge"
    @merged="onMerged"
  />
</template>
